import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import { renameKeysFromSDKRequestToRESTRequest, renameKeysFromRESTResponseToSDKResponse, } from '@wix/sdk-runtime/rename-all-nested-keys';
import * as ambassadorWixEcomV1ShippingOption from './ecom-v1-shipping-option-shipping-options.http.js';
export var ConditionType;
(function (ConditionType) {
    ConditionType["UNKNOWN_TYPE"] = "UNKNOWN_TYPE";
    ConditionType["BY_TOTAL_WEIGHT"] = "BY_TOTAL_WEIGHT";
    ConditionType["BY_TOTAL_PRICE"] = "BY_TOTAL_PRICE";
    ConditionType["BY_TOTAL_QUANTITY"] = "BY_TOTAL_QUANTITY";
})(ConditionType || (ConditionType = {}));
export var LogicalOperator;
(function (LogicalOperator) {
    LogicalOperator["UNKNOWN_LOGICAL_OPERATOR_TYPE"] = "UNKNOWN_LOGICAL_OPERATOR_TYPE";
    LogicalOperator["EQ"] = "EQ";
    LogicalOperator["GT"] = "GT";
    LogicalOperator["GTE"] = "GTE";
    LogicalOperator["LT"] = "LT";
    LogicalOperator["LTE"] = "LTE";
})(LogicalOperator || (LogicalOperator = {}));
export var SortOrder;
(function (SortOrder) {
    SortOrder["ASC"] = "ASC";
    SortOrder["DESC"] = "DESC";
})(SortOrder || (SortOrder = {}));
export var WebhookIdentityType;
(function (WebhookIdentityType) {
    WebhookIdentityType["UNKNOWN"] = "UNKNOWN";
    WebhookIdentityType["ANONYMOUS_VISITOR"] = "ANONYMOUS_VISITOR";
    WebhookIdentityType["MEMBER"] = "MEMBER";
    WebhookIdentityType["WIX_USER"] = "WIX_USER";
    WebhookIdentityType["APP"] = "APP";
})(WebhookIdentityType || (WebhookIdentityType = {}));
/**
 * Creates a ShippingOption.
 *
 * The request body must include shipping option and delivery region associated to.
 * @param shippingOption - ShippingOption to be created.
 * @public
 * @documentationMaturity preview
 * @requiredField shippingOption
 * @permissionId ECOM.SHIPPING_OPTION_CREATE
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @applicableIdentity APP
 * @returns The created ShippingOption.
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.CreateShippingOption
 */
export async function createShippingOption(shippingOption) {
    // @ts-ignore
    const { httpClient, sideEffects } = arguments[1];
    const payload = renameKeysFromSDKRequestToRESTRequest({
        shippingOption: shippingOption,
    });
    const reqOpts = ambassadorWixEcomV1ShippingOption.createShippingOption(payload);
    sideEffects?.onSiteCall?.();
    try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return renameKeysFromRESTResponseToSDKResponse(result.data)
            ?.shippingOption;
    }
    catch (err) {
        const transformedError = sdkTransformError(err, {
            spreadPathsToArguments: {},
            explicitPathsToArguments: { shippingOption: '$[0]' },
            singleArgumentUnchanged: false,
        }, ['shippingOption']);
        sideEffects?.onError?.(err);
        throw transformedError;
    }
}
/**
 * Retrieves a ShippingOption.
 * @param shippingOptionId - ID of the ShippingOption to retrieve.
 * @public
 * @documentationMaturity preview
 * @requiredField shippingOptionId
 * @permissionId ECOM.SHIPPING_OPTION_READ
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Read eCommerce - all read permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.READ-ECOM
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @applicableIdentity APP
 * @returns The requested ShippingOption.
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.GetShippingOption
 */
export async function getShippingOption(shippingOptionId) {
    // @ts-ignore
    const { httpClient, sideEffects } = arguments[1];
    const payload = renameKeysFromSDKRequestToRESTRequest({
        shippingOptionId: shippingOptionId,
    });
    const reqOpts = ambassadorWixEcomV1ShippingOption.getShippingOption(payload);
    sideEffects?.onSiteCall?.();
    try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return renameKeysFromRESTResponseToSDKResponse(result.data)
            ?.shippingOption;
    }
    catch (err) {
        const transformedError = sdkTransformError(err, {
            spreadPathsToArguments: {},
            explicitPathsToArguments: { shippingOptionId: '$[0]' },
            singleArgumentUnchanged: false,
        }, ['shippingOptionId']);
        sideEffects?.onError?.(err);
        throw transformedError;
    }
}
/**
 * Updates a ShippingOption.
 *
 *
 * Each time the ShippingOption is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the ShippingOption.
 * This ensures you're working with the latest ShippingOption
 * and prevents unintended overwrites.
 * @param _id - ShippingOption ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField shippingOption
 * @requiredField shippingOption.revision
 * @permissionId ECOM.SHIPPING_OPTION_UPDATE
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @applicableIdentity APP
 * @returns Updated ShippingOption.
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.UpdateShippingOption
 */
export async function updateShippingOption(_id, shippingOption) {
    // @ts-ignore
    const { httpClient, sideEffects } = arguments[2];
    const payload = renameKeysFromSDKRequestToRESTRequest({
        shippingOption: { ...shippingOption, id: _id },
    });
    const reqOpts = ambassadorWixEcomV1ShippingOption.updateShippingOption(payload);
    sideEffects?.onSiteCall?.();
    try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return renameKeysFromRESTResponseToSDKResponse(result.data)
            ?.shippingOption;
    }
    catch (err) {
        const transformedError = sdkTransformError(err, {
            spreadPathsToArguments: { shippingOption: '$[1]' },
            explicitPathsToArguments: { 'shippingOption.id': '$[0]' },
            singleArgumentUnchanged: false,
        }, ['_id', 'shippingOption']);
        sideEffects?.onError?.(err);
        throw transformedError;
    }
}
/**
 * Deletes a ShippingOption.
 * Deleting a ShippingOption permanently removes them from the ShippingOption List.
 * @param shippingOptionId - Id of the ShippingOption to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField shippingOptionId
 * @permissionId ECOM.SHIPPING_OPTION_DELETE
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @applicableIdentity APP
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.DeleteShippingOption
 */
export async function deleteShippingOption(shippingOptionId) {
    // @ts-ignore
    const { httpClient, sideEffects } = arguments[1];
    const payload = renameKeysFromSDKRequestToRESTRequest({
        shippingOptionId: shippingOptionId,
    });
    const reqOpts = ambassadorWixEcomV1ShippingOption.deleteShippingOption(payload);
    sideEffects?.onSiteCall?.();
    try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
    }
    catch (err) {
        const transformedError = sdkTransformError(err, {
            spreadPathsToArguments: {},
            explicitPathsToArguments: { shippingOptionId: '$[0]' },
            singleArgumentUnchanged: false,
        }, ['shippingOptionId']);
        sideEffects?.onError?.(err);
        throw transformedError;
    }
}
/**
 * Retrieves a list of ShippingOptions, given the provided [paging, filtering, and sorting][1].
 *
 * Up to 1,000 ShippingOptions can be returned per request.
 *
 * To learn how to query ShippingOptions, see [API Query Language][2].
 *
 * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
 * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
 * @public
 * @documentationMaturity preview
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Read eCommerce - all read permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.READ-ECOM
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionId ECOM.SHIPPING_OPTION_READ
 * @applicableIdentity APP
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.QueryShippingOptions
 */
export function queryShippingOptions() {
    // @ts-ignore
    const { httpClient, sideEffects } = arguments[0];
    return queryBuilder({
        func: async (payload) => {
            const reqOpts = ambassadorWixEcomV1ShippingOption.queryShippingOptions(payload);
            sideEffects?.onSiteCall?.();
            try {
                const result = await httpClient.request(reqOpts);
                sideEffects?.onSuccess?.(result);
                return result;
            }
            catch (err) {
                sideEffects?.onError?.(err);
                throw err;
            }
        },
        requestTransformer: (query) => {
            const args = [query, {}];
            return renameKeysFromSDKRequestToRESTRequest({
                ...args?.[1],
                query: args?.[0],
            });
        },
        responseTransformer: ({ data, }) => {
            const transformedData = renameKeysFromRESTResponseToSDKResponse(data);
            return {
                items: transformedData?.shippingOptions,
                pagingMetadata: transformedData?.pagingMetadata,
            };
        },
        errorTransformer: (err) => {
            const transformedError = sdkTransformError(err, {
                spreadPathsToArguments: {},
                explicitPathsToArguments: { query: '$[0]' },
                singleArgumentUnchanged: false,
            });
            throw transformedError;
        },
        pagingMethod: 'CURSOR',
        transformationPaths: {},
    });
}
/**
 * Updates extended fields of a ShippingOption without incrementing revision
 * @param _id - ID of the entity to update.
 * @param namespace - Identifier for the app whose extended fields are being updated.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField namespace
 * @requiredField options
 * @requiredField options.namespaceData
 * @permissionId ECOM.SHIPPING_OPTION_UPDATE
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @applicableIdentity APP
 * @fqn wix.ecom.delivery.v1.ShippingOptionService.UpdateExtendedFields
 */
export async function updateExtendedFields(_id, namespace, options) {
    // @ts-ignore
    const { httpClient, sideEffects } = arguments[3];
    const payload = renameKeysFromSDKRequestToRESTRequest({
        id: _id,
        namespace: namespace,
        namespaceData: options?.namespaceData,
    });
    const reqOpts = ambassadorWixEcomV1ShippingOption.updateExtendedFields(payload);
    sideEffects?.onSiteCall?.();
    try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return renameKeysFromRESTResponseToSDKResponse(result.data);
    }
    catch (err) {
        const transformedError = sdkTransformError(err, {
            spreadPathsToArguments: {},
            explicitPathsToArguments: {
                id: '$[0]',
                namespace: '$[1]',
                namespaceData: '$[2].namespaceData',
            },
            singleArgumentUnchanged: false,
        }, ['_id', 'namespace', 'options']);
        sideEffects?.onError?.(err);
        throw transformedError;
    }
}
//# sourceMappingURL=ecom-v1-shipping-option-shipping-options.universal.js.map